// Add polyfills for Hermes engine compatibility
import 'react-native-url-polyfill/auto';
import 'intl-pluralrules';

// TextEncoder polyfill for QR Code library
import { NavigationContainer, createNavigationContainerRef } from '@react-navigation/native';
import { StatusBar } from 'expo-status-bar';
import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ActivityIndicator } from "react-native";
import { Platform } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { TextEncoder, TextDecoder } from 'text-encoding';
if (typeof global.TextEncoder === 'undefined') {
  global.TextEncoder = TextEncoder;
}
if (typeof global.TextDecoder === 'undefined') {
  global.TextDecoder = TextDecoder;
}

// React Native Web compatibility fix for SDK 51 (web only)
if (Platform.OS === 'web') {
  require('./src/lib/reactNativeWebFix.web');
}

// Environment variables are loaded through Expo Constants

import { getScreenshotLanguage, isScreenshotMode } from './lib/screenshotMode';
import { ThemeProvider } from './src/design-system/ThemeProvider';
import { RTLProvider } from './src/features/localization/components/RTLProvider';
import { useConversionFunnel } from './src/features/monetization/hooks/useConversionFunnel';
import { revenueCatService } from './src/features/monetization/services/revenueCatService';
import OnboardingManager, { shouldShowOnboarding } from './src/features/onboarding/OnboardingManager';
import { useLanguageStore } from "./src/features/quiz-core/languageStore";
import { t } from './src/i18n/translations';
import { initAnalytics, track } from './src/lib/analytics';
import { initFirebase, setCrashlyticsUserId } from './src/lib/firebase';
import { reportInstallOnce } from './src/lib/installAttribution';
import { initSentry } from './src/lib/sentry';
import { supabase } from "./src/lib/supabase";
import './src/lib/warningSuppressions';
import AppNavigator, { linking } from "./src/navigation/AppNavigator";
// Using our analytics wrapper instead of direct amplitude import
import DeepLinkHandler from './src/shared/components/DeepLinkHandler';
import { ErrorBoundary } from './src/shared/components/ErrorBoundary';
import { AppProvider } from './src/shared/contexts/AppContext';
import { GuestUserProvider } from './src/shared/contexts/GuestUserContext';
import { initOneSignal, setExternalUserId, removeExternalUserId } from './src/shared/services/onesignalService';

const navigationRef = createNavigationContainerRef();

export default function App() {
  const screenshotMode = isScreenshotMode();

  // Expose navigation ref globally for screenshot capture script
  useEffect(() => {
    if (screenshotMode && typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__navigationRef = navigationRef;
    }
  }, [screenshotMode]);
  const initializeLanguage = useLanguageStore((state) => state.initializeLanguage);
  const setLanguage = useLanguageStore((state) => state.setLanguage);
  const setLanguageLoading = useLanguageStore((state) => state.setLanguageLoading);
  const loading = useLanguageStore((state) => state.loading);
  const language = useLanguageStore((state) => state.language);

  // Onboarding state management — skip in screenshot mode
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(screenshotMode ? false : true);

  // Conversion funnel tracking
  const { trackAppInstall } = useConversionFunnel();

  // Amplitude initialization is handled in initAnalytics()

  // Initialise Sentry, Analytics, OneSignal once at app start
  useEffect(() => {
    if (screenshotMode) {
      return;
    }

    initSentry();
    initAnalytics();
    initOneSignal();
    // Push permission is requested after the player's first completed quiz
    // (src/lib/pushPrompt.ts), not here: iOS allows the system dialog once per install.
    void initFirebase();

    // Track app install/launch for conversion funnel
    void trackAppInstall();

    // Report Play Store install referrer to Krown attribution (Android, once).
    void reportInstallOnce();

    // Initialize RevenueCat
    void initRevenueCat();

    // Sync OneSignal + Crashlytics user ID with auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user?.id) {
        setExternalUserId(session.user.id);
        setCrashlyticsUserId(session.user.id);
      } else {
        removeExternalUserId();
      }
    });
    return () => subscription.unsubscribe();
  }, [screenshotMode, trackAppInstall]);

  // RevenueCat initialization function
  const initRevenueCat = useCallback(async () => {
    try {
      if (!revenueCatService.isAvailable()) {
        return;
      }

      // Initialize RevenueCat SDK
      await revenueCatService.initialize();
      console.log('✅ RevenueCat initialized successfully');

      // Listen for auth state changes to identify users
      supabase.auth.onAuthStateChange((event, session) => {
        void (async () => {
          try {
            if (event === 'SIGNED_IN' && session?.user?.id) {
              // Identify user with RevenueCat when they sign in
              await revenueCatService.identifyUser(session.user.id);
              console.log('✅ User identified with RevenueCat:', session.user.id.slice(0, 8) + '...');
            } else if (event === 'SIGNED_OUT') {
              // Log out from RevenueCat when user signs out
              await revenueCatService.logout();
              console.log('✅ User logged out from RevenueCat');
            }
          } catch (listenerError) {
            console.error('❌ RevenueCat auth-state sync failed:', listenerError);
          }
        })();
      });
    } catch (error) {
      console.error('❌ Failed to initialize RevenueCat:', error);
    }
  }, []);

  useEffect(() => {
    if (screenshotMode) {
      void setLanguage(getScreenshotLanguage());
      setLanguageLoading(false);
      return;
    }

    // Initialize language from storage on app startup
    void initializeLanguage();
  }, [initializeLanguage, screenshotMode, setLanguage, setLanguageLoading]);

  // Check onboarding status — skip in screenshot mode
  useEffect(() => {
    if (screenshotMode) return;
    async function checkOnboardingStatus() {
      try {
        const shouldShow = await shouldShowOnboarding();
        setShowOnboarding(shouldShow);

        // Track onboarding status
        track('onboarding_status_checked', {
          should_show_onboarding: shouldShow,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        // Default to showing onboarding if we can't determine status
        setShowOnboarding(true);
      } finally {
        setOnboardingLoading(false);
      }
    }

    checkOnboardingStatus();
  }, []);

  // Track app open event
  useEffect(() => {
    track('app_open');
  }, []);

  // Show loading while checking language and onboarding status
  if (loading || onboardingLoading) {
    return (
      <ThemeProvider>
        <View
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#1F2937", // Match app theme
          }}
          data-oid="..go.o."
        >
          <ActivityIndicator size="large" color="#3B82F6" data-oid="q:7hq6k" />
          <Text style={{ color: '#FFFFFF', marginTop: 16, fontSize: 16 }}>
            {t('coreUI.loadingApp', language)}
          </Text>
        </View>
      </ThemeProvider>
    );
  }

  // Show onboarding flow for new users
  if (showOnboarding) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
        <SafeAreaProvider>
          <GuestUserProvider>
            <AppProvider>
              <OnboardingManager
                onComplete={() => {
                  console.log('App.tsx onComplete called - setting showOnboarding to false');
                  setShowOnboarding(false);
                  // Track onboarding completion
                  track('onboarding_completed', {
                    timestamp: new Date().toISOString()
                  });
                  console.log('App.tsx onComplete completed');
                }}
              />
              <StatusBar style="light" />
            </AppProvider>
          </GuestUserProvider>
        </SafeAreaProvider>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
      <SafeAreaProvider>
        <GuestUserProvider>
          <AppProvider>
            <NavigationContainer ref={navigationRef} linking={linking}>
              <RTLProvider>
                <DeepLinkHandler>
                  <AppNavigator data-oid="bsibcgb" />
                </DeepLinkHandler>
              </RTLProvider>
            </NavigationContainer>
            <StatusBar style="auto" data-oid="ud-e2q9" />
          </AppProvider>
        </GuestUserProvider>
      </SafeAreaProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
